import React, { useEffect } from "react";
import {
  Box,
  Avatar,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemButton,
  Badge,
  Divider,
} from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { listenForAuthChanges } from "../../redux/authSlice";
import { listenToUserConversations } from "../../redux/threadsSlice";
import { useNavigate } from "react-router-dom";

const MessagesPreview: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { user } = useSelector((s: RootState) => s.auth);
  const { threads } = useSelector((s: RootState) => s.threads);

useEffect(() => {
  dispatch(listenForAuthChanges());
  const promise = dispatch(listenToUserConversations());

  // cleanup Firestore unsubscribe when leaving component
  return () => {
    promise.then((res: any) => {
      if (typeof res === "function") res(); // unsubscribe()
    });
  };
}, [dispatch]);


  const handleThreadClick = (otherUserId?: string) => {
    if (otherUserId) {
      navigate(`/messages/${otherUserId}`);
    }
  };

  return (
    <Box display="flex" flexDirection="column" height="100vh" bgcolor="#fff">
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={2}
        height={64}
        borderBottom="1px solid #eee"
      >

    
      </Box>

      {/* Threads List */}
      <List sx={{ flexGrow: 1, overflow: "auto", py: 0 }}>
        {threads.map((item) => {
          const otherUserId = item.participants.find(
            (p: string) => p !== user?.userId
          );

          // ✅ fix: guard against undefined userId
          const unreadCount = user?.userId
            ? item.unread?.[user.userId] ?? 0
            : 0;

const lastMsg = item.lastMessage?.text ?? "Start chatting";
          const senderName = item.lastMessage?.senderName || "User";

          // ✅ cast so TS doesn’t complain
          const avatar = (item.lastMessage as any)?.senderProfilePicture;

          return (
            <React.Fragment key={item.id}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleThreadClick(otherUserId)}
                  sx={{ py: 1.5, px: 2 }}
                >
                  <ListItemAvatar>
                    {avatar ? (
                      <Avatar src={avatar} />
                    ) : (
                      <Avatar sx={{ bgcolor: "#eee" }}>
                        <PersonOutlineIcon sx={{ color: "#666" }} />
                      </Avatar>
                    )}
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography
                        variant="subtitle1"
                        fontWeight="600"
                        color="#111"
                      >
                        {senderName}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        variant="body2"
                        color={unreadCount > 0 ? "#000" : "#666"}
                        fontWeight={unreadCount > 0 ? "600" : "400"}
                        noWrap
                      >
                        {lastMsg}
                      </Typography>
                    }
                  />
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-end"
                    minWidth={60}
                  >
                    {item.updatedAt && (
                      <Typography variant="caption" color="#999" mb={0.5}>
                        {new Date(item.updatedAt).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Typography>
                    )}
                    {unreadCount > 0 && (
                      <Badge
                        badgeContent={unreadCount}
                        color="success"
                        sx={{
                          "& .MuiBadge-badge": {
                            backgroundColor: "#25d366",
                            color: "#fff",
                            fontWeight: "700",
                          },
                        }}
                      />
                    )}
                  </Box>
                </ListItemButton>
              </ListItem>
              <Divider sx={{ ml: 9 }} />
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );
};

export default MessagesPreview;
export const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: "flex",
    flex: 1,
    backgroundColor: "#fff",
    flexDirection: "column",
    height: "100vh",
  },

  header: {
    height: 64,
    backgroundColor: "#fff",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 16px",
    borderBottom: "1px solid #eee",
  },

  logo: { fontSize: 20, fontWeight: 700, color: "#111" },

  headerActions: {
    display: "flex",
    flexDirection: "row",
    gap: 20,
    alignItems: "center",
  },

  threadRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: "12px 16px",
    backgroundColor: "#fff",
  },

  avatar: {
    width: 48,
    height: 48,
    borderRadius: "50%",
    marginRight: 12,
  },

  avatarFallback: {
    backgroundColor: "#eee",
    width: 48,
    height: 48,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  threadContent: { flex: 1 },

  threadName: { color: "#111", fontSize: 16, fontWeight: 600 },

  threadLastMsg: { color: "#666", fontSize: 14, marginTop: 2 },

  threadMeta: { display: "flex", alignItems: "flex-end", minWidth: 60 },

  threadTime: { color: "#999", fontSize: 12, marginBottom: 6 },

  unreadBadge: {
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: "#25d366",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "0 5px",
  },

  unreadText: { color: "#fff", fontSize: 12, fontWeight: 700 },

  separator: { height: 0.8, backgroundColor: "#eee", marginLeft: 76 },
};
